import { useSyncExternalStore } from 'react';
import { config } from './config';

/** Analytics consent (CNIL): explicit choice, kept 6 months, then asked again. */
export type ConsentChoice = 'granted' | 'denied';

const KEY = 'gg_consent';
const TTL_MS = 6 * 30 * 24 * 60 * 60 * 1000;

type Stored = { choice: ConsentChoice; at: number };

const listeners = new Set<() => void>();
let reopened = false;

export function parseConsent(raw: string | null, now = Date.now()): ConsentChoice | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<Stored>;
    if (v.choice !== 'granted' && v.choice !== 'denied') return null;
    if (typeof v.at !== 'number' || now - v.at >= TTL_MS || v.at > now) return null;
    return v.choice;
  } catch {
    return null;
  }
}

export function getConsent(): ConsentChoice | null {
  try {
    return parseConsent(localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

function emit() {
  listeners.forEach((l) => l());
}

export function setConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ choice, at: Date.now() } satisfies Stored));
  } catch {
    // Storage blocked: the choice applies for this page view only.
  }
  reopened = false;
  emit();
}

/** Footer link: show the banner again so the choice can be changed. */
export function reopenConsent(): void {
  reopened = true;
  emit();
}

export function subscribeConsent(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** No GA4 ID, no analytics: neither the banner nor the footer link is shown. */
export const consentEnabled = Boolean(config.ga4Id);

/** Banner visibility, plus the current stored choice. */
export function useConsent() {
  const choice = useSyncExternalStore(subscribeConsent, getConsent, () => null);
  const open = useSyncExternalStore(
    subscribeConsent,
    () => consentEnabled && (reopened || getConsent() === null),
    () => false,
  );
  return { choice, open };
}
